// THE SIGN-OFF STAGE: the building as a generated isometric, split by level, area and
// system (owner 2026-11-05) - the Project at a Glance drawing in the sign-off's colours,
// in place of the 3D viewer and the tile cubes.
//
// WHAT IT DRAWS, AND FROM WHERE. The structure is the union of the parsed documents, the
// project's areas and the model export (isoModel.structureFrom), so every level the
// DRAWINGS carry is a plate here even where the model has placed nothing - the first cut
// of this page read the structure off the tile set and showed one of BA03's four levels.
//
//   the plate      the level's own footprint from the drawings, else the footprint of the
//                  model's tiles, else the project's outline, dashed, saying so
//   the rooms      the level's room polygons, filled by category, as the glance draws them
//   the grid       every named line of the drawings' grid, with its bubble (owner: "make
//                  sure the grid lines and names are visible in all isometric views")
//   the layers     every system the drawings draw, ALL ON to begin with, each a chip
//   the areas      every current area of the level: its model footprint where the two
//                  frames are registered, else the grid zone that names it, else listed
//                  under the plate rather than drawn somewhere invented
//
// THE TWO FRAMES. The drawings and the model do not always agree on where the building is:
// on BA03 the manifest's plan y and the tile grid's are ~1,900 ft apart. isoModel's
// registerFrames maps one onto the other through the named grid lines and refuses over a
// 6 in residual; when it refuses, the model's footprints are NOT drawn on the drawings'
// plate and the legend says the placement cannot be shown. A wrong picture is worse than
// an incomplete one.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Tooltip } from "antd";
import { CATEGORY_FILL, bucketKey, dotsPath, linesPath, makeStackProjector, marksPath, polysPath } from "components/Coordination/Glance/glanceModel";
import { COLORS, GREY } from "./signoffState";
import {
  cellBounds, cellPolys, cellsOn, gridFromLines, gridOn, inArea, isLayer, layerIdOf, levelKeyOf,
  mergeBounds, polyBounds, polyPath, polysPathOn, registerFrames, structureFrom, zoneBand, zonesNaming,
} from "./isoModel";

const f1 = (v) => Number(v).toFixed(1);
const norm = (s) => String(s || "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "");

// The colour tokens the drawing uses. Exporting the SVG on its own takes them with it,
// read from what the page actually computed - the stylesheet stays the one definition.
const TOKENS = ["--paper", "--sheet", "--ink", "--ink2", "--line", "--room", "--system", "--systemink",
  "--tel", "--sec", "--pwr", "--ltg", "--fire", "--duct", "--pipe", "--under", "--proc", "--rtutop"];

/** The drawing as a standalone SVG data URL, with its tokens resolved. */
export function svgDataUrl(svg, host) {
  if (!svg) return "";
  const css = window.getComputedStyle(host || svg);
  const vars = TOKENS.map((t) => `${t}:${(css.getPropertyValue(t) || "").trim() || "#888"}`).join(";");
  const copy = svg.cloneNode(true);
  copy.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  const style = document.createElementNS("http://www.w3.org/2000/svg", "style");
  style.textContent = `svg{${vars};background:${(css.getPropertyValue("--sheet") || "#fff").trim()}}`;
  copy.insertBefore(style, copy.firstChild);
  const xml = typeof XMLSerializer === "function" ? new XMLSerializer().serializeToString(copy) : copy.outerHTML;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
}

/** levelKey -> name, the timeline's names first, the drawings filling the gaps. */
export function levelNamesFrom(levels, manifest) {
  const out = { ...(levels || {}) };
  for (const l of (manifest && manifest.levels) || []) {
    const k = levelKeyOf(l.id);
    if (k && !out[k] && l.name) out[k] = l.name;
  }
  return out;
}

/** The glance level id for a level key, so its buckets and rooms can be found. */
export function glanceLevelOf(manifest, levelKey, levelName) {
  const want = levelKeyOf(levelKey);
  const ls = (manifest && manifest.levels) || [];
  const byId = ls.find((l) => levelKeyOf(l.id) === want);
  if (byId) return byId.id;
  const m = /^L(\d+)$/.exec(want);
  const byName = ls.find((l) => {
    const n = norm(l.name);
    return n === want || (!!m && (n === `LEVEL${m[1]}` || n === `L${m[1]}` || n === `FLOOR${m[1]}`));
  });
  if (byName) return byName.id;
  const byGiven = ls.find((l) => norm(l.name) === norm(levelName));
  return byGiven ? byGiven.id : "";
}

/** One system's drawn geometry over a level, in the drawings' own colours. */
function LayerPaths({ manifest, glanceLevel, layer, project, rank, clipId }) {
  const paths = useMemo(() => {
    const buckets = (manifest && manifest.buckets) || {};
    const zones = ((manifest && manifest.zones) || []).map((z) => z.index);
    return (zones.length ? zones : [0]).map((z) => buckets[bucketKey(glanceLevel, layer.id, z)]).filter(Boolean);
  }, [manifest, glanceLevel, layer.id]);
  if (!paths.length) return null;
  const color = `var(${layer.token})`;
  const proj = (x, y) => project(x, y, rank);
  return (
    <g clipPath={clipId ? `url(#${clipId})` : undefined} style={{ pointerEvents: "none" }} data-testid={`iso-layer-${layer.id}`}>
      {paths.map((b, i) => (
        <g key={i}>
          {b.lines ? <path d={linesPath(b.lines, proj)} stroke={color} strokeWidth="0.9" fill="none" /> : null}
          {b.polys ? <path d={polysPath(b.polys, proj)} fill={color} fillOpacity="0.55" stroke={color} strokeWidth="0.4" /> : null}
          {b.dots ? <path d={dotsPath(b.dots, proj, 1)} fill={color} /> : null}
          {b.marks ? <path d={marksPath(b.marks, proj, 3)} fill={color} stroke="var(--sheet)" strokeWidth="0.5" /> : null}
        </g>
      ))}
    </g>
  );
}

/** The named grid lines of a plate, with their bubbles. */
function GridLines({ lines, project, rank }) {
  if (!lines.length) return null;
  return (
    <g style={{ pointerEvents: "none" }} data-testid="iso-grid">
      {lines.map((l) => {
        const a = project(l.from[0], l.from[1], rank);
        const b = project(l.to[0], l.to[1], rank);
        return (
          <g key={`${l.axis}-${l.label}`}>
            <line x1={f1(a[0])} y1={f1(a[1])} x2={f1(b[0])} y2={f1(b[1])} stroke="var(--line)" strokeWidth="0.5" strokeDasharray="4 3" opacity="0.85" />
            <circle cx={f1(b[0])} cy={f1(b[1])} r="6.5" fill="var(--sheet)" stroke="var(--ink2)" strokeWidth="0.7" />
            <text x={f1(b[0])} y={f1(b[1] + 2.4)} textAnchor="middle" fontSize="7" fill="var(--ink)">{String(l.label).slice(0, 4)}</text>
          </g>
        );
      })}
    </g>
  );
}

export default function SignoffIsometric({
  tiles, areas, levelNames = {}, manifest = null, gridLines = null, states, scope,
  onToggleArea = null, onToggleLevel = null, onPickArea = null, selectedAreaId = "",
  focusKeys = null, height = 560, captureRef = null, showGrid = true, onStructure = null,
}) {
  const hostRef = useRef(null);
  const svgRef = useRef(null);
  useEffect(() => {
    if (!captureRef) return undefined;
    captureRef.current = () => svgDataUrl(svgRef.current, hostRef.current);
    return () => { captureRef.current = null; };
  }, [captureRef]);

  const grid = tiles && tiles.grid;
  const list = useMemo(() => (tiles && tiles.tiles) || [], [tiles]);
  const st = useMemo(() => structureFrom({ tiles: list, areas, levelNames, manifest }), [list, areas, levelNames, manifest]);
  useEffect(() => { if (onStructure) onStructure(st); }, [st, onStructure]);

  // ALL LAYERS ON to begin with (owner 2026-11-05: "the same full isometric view, with all
  // layers"); a chip takes one off. A layer the drawings do not carry is not offered.
  const drawable = useMemo(() => st.layers.filter((l) => l.drawn), [st.layers]);
  const [off, setOff] = useState(() => new Set());
  const layersOn = useMemo(() => drawable.filter((l) => !off.has(l.id)), [drawable, off]);

  const pickedAreas = useMemo(() => new Set(scope.areaIds || []), [scope.areaIds]);
  const pickedLevels = useMemo(() => new Set(scope.levelKeys || []), [scope.levelKeys]);
  const pickedLayers = useMemo(() => new Set((scope.systems || []).filter(isLayer).map(layerIdOf)), [scope.systems]);
  const modelSystems = useMemo(() => (scope.systems || []).filter((s) => !isLayer(s)), [scope.systems]);

  // the frame the plates are drawn in, and whether the model's footprints can join them
  const fromDocs = !!(manifest && (manifest.levels || []).length);
  const reg = useMemo(() => (fromDocs ? registerFrames(gridLines || [], manifest.grid) : null), [fromDocs, gridLines, manifest]);
  const canMix = !fromDocs || !!(reg && reg.ok);
  const toFrame = fromDocs && reg && reg.ok ? reg.toDrawing : null;

  const plates = useMemo(() => st.levels.map((l) => {
    const gl = glanceLevelOf(manifest, l.key, l.name);
    const cells = cellsOn(list, l.key);
    // the areas of this level, and any area on NO level that the model has nevertheless
    // placed here: RT06's A and B belong to no level and no zone names them, but the
    // export puts their elements on each floor, so the model says where they are
    const mine = [
      ...(st.areasByLevel.get(l.key) || []),
      ...(st.unlevelled || []).filter((a) => cellsOn(list, l.key, inArea(a.id)).size),
    ];
    const areasHere = mine.map((a) => {
      const own = cellsOn(list, l.key, inArea(a.id));
      if (own.size && canMix) return { ...a, kind: "model", polys: cellPolys(own, grid, toFrame), cells: own };
      const zones = zonesNaming(a.name, manifest);
      if (zones.length) return { ...a, kind: "zone", zones, cells: own };
      return { ...a, kind: own.size ? "unmapped" : "", cells: own };
    });
    return { ...l, glanceLevel: gl, cells, areasHere };
  }), [st, list, manifest, grid, canMix, toFrame]);

  // AN AREA ON NO SINGLE LEVEL that the drawings' zones name runs THROUGH the building -
  // BA03's A to F are its grid zones ("Areas A and D"), and its schedule signs off "L1 ·
  // Area A · Conduits" against them. A zone is a vertical cut, so these band every plate.
  const spanning = useMemo(() => (st.unlevelled || [])
    .map((a) => ({ ...a, zones: zonesNaming(a.name, manifest) }))
    .filter((a) => a.zones.length), [st.unlevelled, manifest]);

  // the plan extent: the drawings' footprints, plus the model's where the frames agree
  const bounds = useMemo(() => {
    const docs = fromDocs ? polyBounds(st.levels.map((l) => l.footprint)) : null;
    const zoneB = fromDocs ? polyBounds(((manifest && manifest.zones) || []).map((z) => [[z.x0, 0], [z.x1, 0]])) : null;
    let model = null;
    if (canMix) {
      const keys = new Set();
      plates.forEach((p) => p.cells.forEach((k) => keys.add(k)));
      if (keys.size) {
        const b = cellBounds(keys, grid);
        model = toFrame
          ? polyBounds([[toFrame(b.xmin, b.ymin), toFrame(b.xmax, b.ymin), toFrame(b.xmax, b.ymax), toFrame(b.xmin, b.ymax)]])
          : b;
      }
    }
    const merged = mergeBounds(mergeBounds(docs, model), docs ? { ...zoneB, ymin: docs.ymin, ymax: docs.ymax, w: 1, h: 1 } : null);
    return merged || { xmin: 0, ymin: 0, xmax: 1, ymax: 1, w: 1, h: 1 };
  }, [fromDocs, st.levels, manifest, canMix, plates, grid, toFrame]);

  const { project, width, height: svgH } = useMemo(
    () => makeStackProjector(bounds, Math.max(1, plates.length), { width: 1140 }), [bounds, plates.length],
  );
  // the grid in the frame the plates are actually drawn in: the drawings' own when the
  // plates came from the drawings, else the model's named lines
  const gridMarks = useMemo(() => {
    if (!showGrid) return [];
    return fromDocs ? gridOn(manifest.grid, bounds) : gridFromLines(gridLines, bounds);
  }, [showGrid, fromDocs, manifest, gridLines, bounds]);

  const stateOf = useCallback((key) => states.get(key) || GREY, [states]);
  const focusCells = useMemo(() => {
    if (!focusKeys) return null;
    const want = new Set(focusKeys);
    const out = new Map();
    for (const t of list) {
      if (!want.has(t.key)) continue;
      for (const g of t.g || []) {
        if (!g.l) continue;
        if (!out.has(g.l)) out.set(g.l, new Set());
        out.get(g.l).add(`${t.i},${t.j}`);
      }
    }
    return out;
  }, [focusKeys, list]);

  const anyPicked = pickedAreas.size > 0 || pickedLevels.size > 0;
  const only = !!scope.only && anyPicked;
  const shown = only
    ? plates.filter((p) => pickedLevels.has(p.key) || p.areasHere.some((a) => pickedAreas.has(a.id)))
    : plates;

  const areaFill = (a, levelKey) => {
    const s = Math.max(stateOf(`area|${a.id}`), stateOf(`level|${levelKey}`));
    return COLORS[s];
  };
  const click = (e, id) => {
    // ctrl / cmd click adds and removes from the scope (owner 2026-11-05); a plain click
    // opens the area beside the drawing
    if ((e.ctrlKey || e.metaKey || e.shiftKey) && onToggleArea) onToggleArea(id);
    else if (onPickArea) onPickArea(id);
  };

  if (!plates.length) {
    return (
      <div className="signoff-iso" data-testid="signoff-iso">
        <p className="signoff-muted" style={{ padding: 12 }}>
          No level is known yet - neither the drawings nor the model export place anything on one.
        </p>
      </div>
    );
  }
  return (
    <div className="signoff-iso" ref={hostRef} data-testid="signoff-iso">
      {drawable.length ? (
        <div className="iso-chips" data-testid="iso-chips">
          <span className="signoff-muted">Layers</span>
          {drawable.map((l) => (
            <Tooltip key={l.id} title={`${l.glanceLabel || l.label} · drawn on ${l.levels.join(", ") || "no level"}${off.has(l.id) ? " · hidden" : ""}`}>
              <button type="button" className={`iso-chip ${off.has(l.id) ? "is-off" : ""}`} data-testid={`iso-chip-${l.id}`}
                      onClick={() => setOff((p) => { const n = new Set(p); if (n.has(l.id)) n.delete(l.id); else n.add(l.id); return n; })}>
                <i style={{ background: `var(${l.token})` }} /> {l.label}
              </button>
            </Tooltip>
          ))}
          <button type="button" className="iso-chip is-plain" data-testid="iso-chips-all" onClick={() => setOff(new Set())}>All on</button>
        </div>
      ) : null}
      <div style={{ maxHeight: height, overflow: "auto" }}>
        <svg viewBox={`0 0 ${width} ${svgH}`} width="100%" style={{ display: "block" }} ref={svgRef} role="img"
             aria-label="The building by level, area and system, coloured by sign-off">
          <defs>
            {shown.map((p) => {
              const picked = p.areasHere.filter((a) => pickedAreas.has(a.id) && (a.polys || a.zones));
              if (!picked.length) return null;
              const d = picked.map((a) => (a.polys
                ? polysPathOn(a.polys, project, p.rank)
                : (a.zones || []).map((z) => polyPath(zoneBand(z, bounds), project, p.rank)).join(""))).join("");
              return d ? <clipPath key={p.key} id={`iso-clip-${p.key}`}><path d={d} /></clipPath> : null;
            })}
          </defs>
          {shown.map((p) => {
            const onLevel = pickedLevels.has(p.key);
            const clipId = p.areasHere.some((a) => pickedAreas.has(a.id)) ? `iso-clip-${p.key}` : "";
            const plate = p.footprint && p.footprint.length >= 3
              ? polyPath(p.footprint, project, p.rank)
              : p.cells.size && canMix ? polysPathOn(cellPolys(p.cells, grid, toFrame), project, p.rank) : "";
            // a level the drawings carry with no outline of its own (BA03's L3) is drawn on
            // the project's extent, dashed, and says so
            const ghost = !plate;
            const outline = ghost
              ? polyPath([[bounds.xmin, bounds.ymin], [bounds.xmax, bounds.ymin], [bounds.xmax, bounds.ymax], [bounds.xmin, bounds.ymax]], project, p.rank)
              : plate;
            const label = project((bounds.xmin + bounds.xmax) / 2, bounds.ymax, p.rank);
            return (
              <g key={p.key} data-testid={`iso-level-${p.key}`}>
                <path d={outline} fill="var(--room)" fillOpacity={ghost ? 0.12 : onLevel ? 0.95 : 0.55}
                      stroke="var(--line)" strokeWidth="0.5" strokeDasharray={ghost ? "6 4" : undefined} />
                {/* the rooms, as the glance fills them */}
                {(p.rooms || []).length ? (
                  <path d={(p.rooms || []).map((r) => polyPath(r.polygon, project, p.rank)).join("")}
                        fill={CATEGORY_FILL.circ || "var(--room)"} fillOpacity="0.5" stroke="var(--line)" strokeWidth="0.25"
                        style={{ pointerEvents: "none" }} data-testid={`iso-rooms-${p.key}`} />
                ) : null}
                {showGrid ? <GridLines lines={gridMarks} project={project} rank={p.rank} /> : null}
                {/* every area of this level: placed where it is known, listed where it is not */}
                {p.areasHere.map((a) => {
                  const on = pickedAreas.has(a.id) || (onLevel && !pickedAreas.size);
                  const sel = selectedAreaId === a.id;
                  const faint = only && !on;
                  const d = a.polys
                    ? polysPathOn(a.polys, project, p.rank)
                    : (a.zones || []).map((z) => polyPath(zoneBand(z, bounds), project, p.rank)).join("");
                  if (!d) return null;
                  return (
                    <path key={a.id} d={d} data-testid={`iso-area-${a.id}`}
                          fill={faint ? "var(--room)" : areaFill(a, p.key)}
                          fillOpacity={faint ? 0.2 : on ? 0.8 : a.kind === "zone" ? 0.3 : 0.5}
                          stroke={on || sel ? "#e8590c" : "var(--ink2)"} strokeWidth={on || sel ? 1.6 : 0.5}
                          strokeDasharray={a.kind === "zone" ? "5 3" : undefined}
                          style={{ cursor: "pointer" }} onClick={(e) => click(e, a.id)}>
                      <title>
                        {`${a.name} · ${p.name} — ${a.kind === "model" ? "placed from the model" : a.kind === "zone" ? "from the grid zone that names it" : "not located"}`}
                        {" — click to open it, ctrl-click to add or remove it from the scope"}
                      </title>
                    </path>
                  );
                })}
                {/* the areas that run through the building, on this plate too */}
                {spanning.map((a) => {
                  const d = a.zones.map((z) => polyPath(zoneBand(z, bounds), project, p.rank)).join("");
                  if (!d) return null;
                  const on = pickedAreas.has(a.id);
                  return (
                    <path key={`span-${a.id}`} d={d} data-testid={`iso-area-${a.id}`}
                          fill={on ? areaFill(a, p.key) : "none"} fillOpacity={on ? 0.45 : 0}
                          stroke={on ? "#e8590c" : "var(--ink2)"} strokeWidth={on ? 1.4 : 0.4} strokeDasharray="5 3"
                          style={{ cursor: "pointer" }} onClick={(e) => click(e, a.id)}>
                      <title>{`${a.name} - a zone the drawings name, through every level. Click to open it, ctrl-click to add or remove it from the scope.`}</title>
                    </path>
                  );
                })}
                {focusCells && focusCells.get(p.key) && canMix ? (
                  <path d={polysPathOn(cellPolys([...focusCells.get(p.key)], grid, toFrame), project, p.rank)}
                        fill="none" stroke="#111827" strokeWidth="1.2" strokeDasharray="3 2"
                        style={{ pointerEvents: "none" }} data-testid={`iso-focus-${p.key}`} />
                ) : null}
                {/* the model's own systems picked, where the frames allow it */}
                {canMix ? modelSystems.map((s) => {
                  const cells = cellsOn(list, p.key, (g) => (g.s || []).some((x) => String(x).toLowerCase() === String(s).toLowerCase())
                    && (!pickedAreas.size || [...pickedAreas].some((id) => inArea(id)(g))));
                  if (!cells.size) return null;
                  return <path key={s} d={polysPathOn(cellPolys(cells, grid, toFrame), project, p.rank)} fill="none"
                               stroke="var(--system)" strokeWidth="1" strokeOpacity="0.9" style={{ pointerEvents: "none" }} />;
                }) : null}
                {/* every drawn layer, unless its chip is off */}
                {p.glanceLevel ? layersOn
                  .filter((l) => !pickedLayers.size || pickedLayers.has(l.id))
                  .map((l) => (
                    <LayerPaths key={l.id} manifest={manifest} glanceLevel={p.glanceLevel} layer={l}
                                project={project} rank={p.rank} clipId={pickedLayers.has(l.id) ? clipId : ""} />
                  )) : null}
                <text x={f1(label[0])} y={f1(label[1] + 16)} textAnchor="middle" fontSize="15" fontWeight="600" fill="var(--ink)"
                      paintOrder="stroke" stroke="var(--sheet)" strokeWidth="4" data-testid={`iso-level-name-${p.key}`}
                      style={{ cursor: onToggleLevel ? "pointer" : "default" }}
                      onClick={() => { if (onToggleLevel) onToggleLevel(p.key); }}>
                  {p.name}{ghost ? " · outline not in the drawings" : ""}
                  <title>
                    {`${p.name} - ${[p.fromDocs ? "in the drawings" : "", p.fromModel ? `${p.elements.toLocaleString()} placed elements` : "nothing placed by the model", p.fromAreas ? "has drawn areas" : ""].filter(Boolean).join(", ")}. Click to put the whole level in the scope.`}
                  </title>
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      {fromDocs && !canMix ? (
        <p className="signoff-muted" style={{ padding: "4px 10px 8px" }} data-testid="iso-frames-note">
          The drawings and the model export do not share a plan frame on this project, and there is no
          named grid on both sides to line them up, so the areas are shown where the drawings put them
          and the model&apos;s own footprints are not drawn over them.
        </p>
      ) : null}
      {plates.some((p) => p.areasHere.some((a) => !a.polys && !(a.zones || []).length)) ? (
        <div className="iso-unplaced" data-testid="iso-unplaced">
          <span className="signoff-muted">Not located on the drawings, still signable:</span>
          {plates.flatMap((p) => p.areasHere.filter((a) => !a.polys && !(a.zones || []).length).map((a) => (
            <button type="button" key={`${p.key}-${a.id}`} className={`iso-chip ${pickedAreas.has(a.id) ? "is-on" : ""}`}
                    data-testid={`iso-unplaced-${a.id}`} onClick={(e) => click(e, a.id)}>
              {a.name} · {p.name}
            </button>
          )))}
        </div>
      ) : null}
      {st.unlevelled.length ? (
        <div className="iso-unplaced" data-testid="iso-unlevelled">
          <span className="signoff-muted">On no single level:</span>
          {st.unlevelled.map((a) => (
            <button type="button" key={a.id} className={`iso-chip ${pickedAreas.has(a.id) ? "is-on" : ""}`}
                    data-testid={`iso-unlevelled-${a.id}`} onClick={(e) => click(e, a.id)}>{a.name}</button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
